import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useInapiApplicationService from '../../app/services/hooks/useInapiApplicationService';
import { getBaseUrl } from '../../app/services/EndPoints';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import InputField from '../../components/InputField';
import logoMonere from '../../assets/logo-monere-fondo-oscuro.png';
import PDFSignatureEditor from '../../components/PDFSignatureEditor/PDFSignatureEditor';

interface RouteParams {
    token: string;
}

interface PowerSignatureData {
    already_signed: boolean;
    brand_name: string;
    application_number?: string;
    signer_name?: string;
    signer_email?: string;
    assigned_lawyer?: string | null;
    document_url?: string;
    has_account?: boolean;
}

/**
 * Pantalla pública de firma del poder simple.
 *
 * El solicitante llega por el enlace que le envía su abogado y firma sin necesidad
 * de tener cuenta, igual que en la firma de contratos. Si no la tiene, puede crearla
 * en el mismo paso.
 */
const SignPower: React.FC = () => {
    const { token } = useParams<RouteParams>();
    const {
        getPowerSignatureByToken, fetchingPowerSignature,
        signPowerByToken, fetchingSignPower
    } = useInapiApplicationService();

    const [data, setData] = useState<PowerSignatureData | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showEditor, setShowEditor] = useState(false);
    // Evita mandar la firma dos veces mientras se sube el PDF
    const submittingRef = useRef(false);
    const [signed, setSigned] = useState(false);

    const [createAccount, setCreateAccount] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');

    useEffect(() => {
        if (!token) return;

        getPowerSignatureByToken(token, {
            onSuccess: (response) => setData(response.data),
            onError: (response) => setLoadError(response.message || 'No se pudo cargar el documento')
        });
    }, [token]);

    const absoluteDocumentUrl = () => {
        const url = data?.document_url || '';
        return url.startsWith('http') ? url : `${getBaseUrl()}${url}`;
    };

    const handleOpenEditor = () => {
        if (createAccount) {
            if (password.length < 8) {
                toast.error('La contraseña debe tener al menos 8 caracteres');
                return;
            }
            if (password !== passwordConfirm) {
                toast.error('Las contraseñas no coinciden');
                return;
            }
        }
        setShowEditor(true);
    };

    const handleSign = (signedPdfBlob: Blob) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setShowEditor(false);
        const body = new FormData();
        body.append('signed_pdf', signedPdfBlob, 'poder_simple_firmado.pdf');
        body.append('create_account', createAccount ? 'true' : 'false');
        if (createAccount) body.append('password', password);

        signPowerByToken(token, body, {
            onSuccess: () => {
                submittingRef.current = false;
                setSigned(true);
                toast.success('Poder simple firmado correctamente');
            },
            onError: (response) => {
                submittingRef.current = false;
                toast.error(response.message || 'No se pudo guardar la firma');
            }
        });
    };

    // Página pública: lleva la marca del estudio que atiende al cliente, o la de Monere
    const branding = (data as any)?.branding as { name?: string; logo_url?: string | null; primary_color?: string } | undefined;
    const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
            <div style={{ background: branding?.primary_color || '#001849', padding: '18px 0' }}>
                <div className="container text-center">
                    {branding?.logo_url ? (
                        <img src={branding.logo_url} alt={branding.name} height="48" style={{ background: '#fff', borderRadius: 8, padding: 4, maxWidth: 220, objectFit: 'contain' }} />
                    ) : branding?.name && branding.name !== 'Monere' ? (
                        <span className="text-white fw-bold fs-4">{branding.name}</span>
                    ) : (
                        <img src={logoMonere} alt="Monere" height="44" />
                    )}
                </div>
            </div>
            <div className="container py-5" style={{ maxWidth: 720 }}>
                <div className="card shadow-sm">
                    <div className="card-body p-4 p-md-5">{children}</div>
                </div>
            </div>
        </div>
    );

    if (fetchingPowerSignature && !data) {
        return (
            <Shell>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </Shell>
        );
    }

    if (loadError) {
        return (
            <Shell>
                <div className="text-center">
                    <i className="fas fa-exclamation-triangle text-warning mb-3" style={{ fontSize: '2.5rem' }}></i>
                    <h4>No pudimos abrir el documento</h4>
                    <p className="text-muted mb-0">{loadError}</p>
                </div>
            </Shell>
        );
    }

    if (signed || data?.already_signed) {
        return (
            <Shell>
                <div className="text-center">
                    <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: '2.5rem' }}></i>
                    <h4>Poder simple firmado</h4>
                    <p className="text-muted">
                        Listo. Tu abogado ya puede presentar la solicitud de la marca
                        {data?.brand_name ? <strong> {data.brand_name}</strong> : null} ante INAPI.
                    </p>
                </div>
            </Shell>
        );
    }

    if (!data) return null;

    return (
        <>
            <Shell>
                <h3 className="mb-1">Firma del poder simple</h3>
                <p className="text-muted">
                    Este documento autoriza a tu abogado a presentar el registro de tu marca ante INAPI.
                </p>

                <div className="bg-light rounded p-3 my-4">
                    <div className="row g-2 small">
                        <div className="col-sm-6">
                            <span className="text-muted d-block">Marca</span>
                            <strong>{data.brand_name}</strong>
                        </div>
                        <div className="col-sm-6">
                            <span className="text-muted d-block">N° de solicitud</span>
                            <strong>{data.application_number || '-'}</strong>
                        </div>
                        <div className="col-sm-6">
                            <span className="text-muted d-block">Firmante</span>
                            <strong>{data.signer_name || '-'}</strong>
                        </div>
                        <div className="col-sm-6">
                            <span className="text-muted d-block">Abogado a cargo</span>
                            <strong>{data.assigned_lawyer || 'Sin asignar'}</strong>
                        </div>
                    </div>
                </div>

                {!data.has_account && (
                    <div className="border rounded p-3 mb-4">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="createAccount"
                                checked={createAccount}
                                onChange={(e) => setCreateAccount(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="createAccount">
                                Quiero crear una cuenta para seguir mi solicitud
                            </label>
                        </div>
                        <p className="text-muted small mb-0 mt-1">
                            Es opcional: puedes firmar sin crear cuenta.
                        </p>

                        {createAccount && (
                            <div className="row mt-3">
                                <div className="col-md-6 mb-2">
                                    <InputField
                                        type="password"
                                        label="Contraseña"
                                        value={password}
                                        onChange={(value: string) => setPassword(value)}
                                        placeholder="Mínimo 8 caracteres"
                                    />
                                </div>
                                <div className="col-md-6 mb-2">
                                    <InputField
                                        type="password"
                                        label="Repetir contraseña"
                                        value={passwordConfirm}
                                        onChange={(value: string) => setPasswordConfirm(value)}
                                        placeholder="Repite la contraseña"
                                    />
                                </div>
                                <div className="col-12">
                                    <p className="text-muted small mb-0">
                                        Tu usuario será <strong>{data.signer_email}</strong>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <ButtonGeneral
                    type="button"
                    variant="primary"
                    onClick={handleOpenEditor}
                    disabled={fetchingSignPower}
                    title="Revisar y firmar documento"
                    icon="fas fa-file-signature"
                />
            </Shell>

            {showEditor && (
                <PDFSignatureEditor
                    pdfUrl={absoluteDocumentUrl()}
                    documentTitle={`Poder simple - ${data.brand_name}`}
                    userName={data.signer_name}
                    onSign={handleSign}
                    onCancel={() => setShowEditor(false)}
                    isVisible={showEditor}
                />
            )}
        </>
    );
};

export default SignPower;
